/**
 * UnitConverterPopover — inline lesson-side unit converter for the
 * Lesson Tools sidebar. NOT a new page or route; renders as a popover
 * anchored to a trigger button so learners can quickly convert between
 * pressure and temperature units without leaving the lesson.
 *
 *   Pressure:     mTorr, Torr, atm, psi, kPa, bar, Pa, mbar
 *   Temperature:  Celsius, Fahrenheit, Kelvin
 *
 * Keeps the last 3 conversions per category in localStorage so learners
 * can compare batch parameters at a glance and instantly recall any
 * previous conversion into the working row.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Calculator, ArrowRightLeft, Thermometer, Gauge, History, X } from 'lucide-react';

// Every pressure unit expressed as a multiplier of Pascals.
// 1 <unit> = <factor> Pa
const PRESSURE_TO_PA = {
  Pa:    1,
  kPa:   1_000,
  bar:   100_000,
  mbar:  100,
  atm:   101_325,
  psi:   6_894.757_293_168_36,
  Torr:  133.322_387_415,
  mTorr: 0.133_322_387_415,
};

const PRESSURE_UNITS = Object.keys(PRESSURE_TO_PA);

const HISTORY_KEY = 'lms:unit-converter-history';
const HISTORY_LIMIT = 3;

// Formatter that avoids scientific notation for typical FDA numbers but
// gracefully falls back to fixed decimals for very small / large values.
const fmt = (n) => {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs === 0) return '0';
  if (abs >= 1000)   return n.toFixed(2).replace(/\.?0+$/, '');
  if (abs >= 1)      return n.toFixed(3).replace(/\.?0+$/, '');
  if (abs >= 0.001)  return n.toFixed(5).replace(/\.?0+$/, '');
  return n.toExponential(3);
};

const convertPressure = (value, from, to) => {
  const pa = value * PRESSURE_TO_PA[from];
  return pa / PRESSURE_TO_PA[to];
};

const convertTemperature = (value, from, to) => {
  let k;
  if (from === 'C') k = value + 273.15;
  else if (from === 'F') k = (value - 32) * (5 / 9) + 273.15;
  else k = value;
  if (to === 'C') return k - 273.15;
  if (to === 'F') return (k - 273.15) * (9 / 5) + 32;
  return k;
};

// Persist / read history — keeps two arrays (pressure, temperature),
// each capped at HISTORY_LIMIT entries. Newest first.
const readHistory = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(HISTORY_KEY) || '{}');
    return {
      pressure:    Array.isArray(parsed.pressure)    ? parsed.pressure.slice(0, HISTORY_LIMIT)    : [],
      temperature: Array.isArray(parsed.temperature) ? parsed.temperature.slice(0, HISTORY_LIMIT) : [],
    };
  } catch {
    return { pressure: [], temperature: [] };
  }
};
const writeHistory = (h) => {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(h)); } catch {}
};

const HistoryList = ({ items, onRecall, onRemove, testidPrefix }) => {
  if (!items?.length) return null;
  return (
    <div className="mt-2" data-testid={`${testidPrefix}-history`}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1 inline-flex items-center gap-1.5">
        <History className="w-3 h-3" /> Recent
      </div>
      <ul className="space-y-1">
        {items.map((h, i) => (
          <li key={`${testidPrefix}-${h.savedAt || i}`} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onRecall(h)}
              className="flex-1 text-left text-[11px] font-mono px-2 py-1 rounded border border-border bg-muted/30 hover:bg-muted/60 truncate"
              title="Recall into the row above"
              data-testid={`${testidPrefix}-history-recall-${i}`}
            >
              {fmt(h.from.value)} {h.from.unit} → {fmt(h.to.value)} {h.to.unit}
            </button>
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="text-muted-foreground hover:text-foreground p-0.5"
              title="Remove from history"
              data-testid={`${testidPrefix}-history-remove-${i}`}
            >
              <X className="w-3 h-3" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PressureBlock = ({ history, updateHistory }) => {
  const [value, setValue] = useState('100');
  const [from, setFrom] = useState('mTorr');
  const [to, setTo] = useState('Torr');

  const num = Number(value);
  const converted = useMemo(
    () => (Number.isFinite(num) ? convertPressure(num, from, to) : NaN),
    [num, from, to],
  );

  // Debounce-save a valid conversion into history whenever the user
  // pauses their inputs for 1 s — avoids spamming the list with every
  // keystroke while still capturing "real" conversions organically.
  useEffect(() => {
    if (!Number.isFinite(num) || !Number.isFinite(converted)) return undefined;
    const t = setTimeout(() => {
      updateHistory({
        from: { value: num, unit: from },
        to:   { value: converted, unit: to },
        savedAt: Date.now(),
      });
    }, 1000);
    return () => clearTimeout(t);
  }, [num, from, to, converted, updateHistory]);

  const swap = () => { setFrom(to); setTo(from); };

  const recall = (h) => {
    setValue(String(h.from.value));
    setFrom(h.from.unit);
    setTo(h.to.unit);
  };

  return (
    <div className="space-y-2" data-testid="unit-converter-pressure">
      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1.5">
        <Gauge className="w-3.5 h-3.5" /> Pressure
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div>
          <Input type="number" step="any" value={value} onChange={(e) => setValue(e.target.value)} className="h-9" data-testid="unit-converter-pressure-input" />
          <select value={from} onChange={(e) => setFrom(e.target.value)} className="w-full mt-1 h-8 px-2 rounded border border-input bg-background text-xs" data-testid="unit-converter-pressure-from">
            {PRESSURE_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <Button type="button" size="icon" variant="ghost" onClick={swap} className="h-8 w-8 rounded-full" title="Swap units" data-testid="unit-converter-pressure-swap">
          <ArrowRightLeft className="w-3.5 h-3.5" />
        </Button>
        <div>
          <Input readOnly value={fmt(converted)} className="h-9 bg-muted/40 font-mono" data-testid="unit-converter-pressure-output" />
          <select value={to} onChange={(e) => setTo(e.target.value)} className="w-full mt-1 h-8 px-2 rounded border border-input bg-background text-xs" data-testid="unit-converter-pressure-to">
            {PRESSURE_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </div>
      <HistoryList
        items={history}
        onRecall={recall}
        onRemove={(i) => updateHistory(null, i)}
        testidPrefix="unit-converter-pressure"
      />
    </div>
  );
};

const TemperatureBlock = ({ history, updateHistory }) => {
  const [value, setValue] = useState('-40');
  const [from, setFrom] = useState('C');
  const [to, setTo] = useState('F');

  const num = Number(value);
  const converted = useMemo(
    () => (Number.isFinite(num) ? convertTemperature(num, from, to) : NaN),
    [num, from, to],
  );

  useEffect(() => {
    if (!Number.isFinite(num) || !Number.isFinite(converted)) return undefined;
    const t = setTimeout(() => {
      updateHistory({
        from: { value: num, unit: from },
        to:   { value: converted, unit: to },
        savedAt: Date.now(),
      });
    }, 1000);
    return () => clearTimeout(t);
  }, [num, from, to, converted, updateHistory]);

  const swap = () => { setFrom(to); setTo(from); };

  const recall = (h) => {
    setValue(String(h.from.value));
    setFrom(h.from.unit);
    setTo(h.to.unit);
  };

  return (
    <div className="space-y-2" data-testid="unit-converter-temperature">
      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1.5">
        <Thermometer className="w-3.5 h-3.5" /> Temperature
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div>
          <Input type="number" step="any" value={value} onChange={(e) => setValue(e.target.value)} className="h-9" data-testid="unit-converter-temperature-input" />
          <select value={from} onChange={(e) => setFrom(e.target.value)} className="w-full mt-1 h-8 px-2 rounded border border-input bg-background text-xs" data-testid="unit-converter-temperature-from">
            <option value="C">°C</option>
            <option value="F">°F</option>
            <option value="K">K</option>
          </select>
        </div>
        <Button type="button" size="icon" variant="ghost" onClick={swap} className="h-8 w-8 rounded-full" title="Swap units" data-testid="unit-converter-temperature-swap">
          <ArrowRightLeft className="w-3.5 h-3.5" />
        </Button>
        <div>
          <Input readOnly value={fmt(converted)} className="h-9 bg-muted/40 font-mono" data-testid="unit-converter-temperature-output" />
          <select value={to} onChange={(e) => setTo(e.target.value)} className="w-full mt-1 h-8 px-2 rounded border border-input bg-background text-xs" data-testid="unit-converter-temperature-to">
            <option value="C">°C</option>
            <option value="F">°F</option>
            <option value="K">K</option>
          </select>
        </div>
      </div>
      <HistoryList
        items={history}
        onRecall={recall}
        onRemove={(i) => updateHistory(null, i)}
        testidPrefix="unit-converter-temperature"
      />
    </div>
  );
};

const UnitConverterPopover = () => {
  const [history, setHistory] = useState(() => readHistory());

  // Pass a single updater to both blocks that either PREPENDS a new
  // entry or REMOVES one by index. `entry === null` + `removeIndex`
  // is the remove path.
  const updatePressureHistory = (entry, removeIndex) => {
    setHistory((prev) => {
      let next = prev.pressure.slice();
      if (entry === null && typeof removeIndex === 'number') {
        next.splice(removeIndex, 1);
      } else if (entry) {
        // Dedupe: skip if the newest entry is already identical.
        const top = next[0];
        if (top && top.from.value === entry.from.value
            && top.from.unit === entry.from.unit
            && top.to.unit === entry.to.unit) {
          return prev;
        }
        next = [entry, ...next].slice(0, HISTORY_LIMIT);
      }
      const merged = { ...prev, pressure: next };
      writeHistory(merged);
      return merged;
    });
  };
  const updateTemperatureHistory = (entry, removeIndex) => {
    setHistory((prev) => {
      let next = prev.temperature.slice();
      if (entry === null && typeof removeIndex === 'number') {
        next.splice(removeIndex, 1);
      } else if (entry) {
        const top = next[0];
        if (top && top.from.value === entry.from.value
            && top.from.unit === entry.from.unit
            && top.to.unit === entry.to.unit) {
          return prev;
        }
        next = [entry, ...next].slice(0, HISTORY_LIMIT);
      }
      const merged = { ...prev, temperature: next };
      writeHistory(merged);
      return merged;
    });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start"
          data-testid="lesson-tools-unit-converter"
        >
          <Calculator className="w-4 h-4 mr-2" />
          Unit converter
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="start"
        className="w-80 space-y-4"
        data-testid="unit-converter-popover"
      >
        <div>
          <div className="font-heading text-sm font-extrabold inline-flex items-center gap-1.5">
            <Calculator className="w-4 h-4 text-primary" /> Quick unit converter
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Pressure and temperature — instant, no round-trip to a calculator page.
          </div>
        </div>
        <PressureBlock history={history.pressure} updateHistory={updatePressureHistory} />
        <TemperatureBlock history={history.temperature} updateHistory={updateTemperatureHistory} />
        <div className="text-[10px] text-muted-foreground border-t border-border pt-2">
          Reference: 1 Torr = 133.322 Pa · 1 psi = 6 894.757 Pa · 1 atm = 101 325 Pa
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default UnitConverterPopover;
